import { client, handleRequestBody } from "./client.ts";

export type WorksiteStatus = "active" | "paused" | "archived";

export interface WorksiteAddress {
	street: string;
	number: string;
	complement?: string;
	neighborhood: string;
	city: string;
	state: string;
	zipcode: string;
	lat?: number;
	lng?: number;
}

export interface Worksite {
	id: string;
	slug?: string;
	account_id: string;
	name: string;
	description?: string;
	status: WorksiteStatus;
	address: WorksiteAddress;
	area_sqm?: number;
	start_date?: string;
	expected_end_date?: string;
	closed_at?: string;
	cover_image_url?: string;
	timezone: string;
	created_at: string;
	updated_at: string;
}

export interface ListWorksitesParams {
	page?: number;
	pageSize?: number;
}

export interface ListWorksitesResponse {
	count: number;
	items: Worksite[];
}

export function listWorksites(
	params: ListWorksitesParams = {},
): Promise<ListWorksitesResponse> {
	return handleRequestBody(() =>
		client.get<ListWorksitesResponse>("/worksites", {
			params: { page: params.page, page_size: params.pageSize },
		}),
	);
}

export interface CreateWorksiteInput {
	name: string;
	description?: string;
	address: WorksiteAddress;
	areaSqm?: number;
	startDate?: string;
	expectedEndDate?: string;
	coverImageUrl?: string;
}

interface WorksiteResponse {
	worksite: Worksite;
}

export async function createWorksite(
	input: CreateWorksiteInput,
): Promise<Worksite> {
	const { worksite } = await handleRequestBody(() =>
		client.post<WorksiteResponse>("/worksites", {
			name: input.name,
			description: input.description,
			address: input.address,
			area_sqm: input.areaSqm,
			start_date: input.startDate,
			expected_end_date: input.expectedEndDate,
			cover_image_url: input.coverImageUrl,
		}),
	);
	return worksite;
}

export interface UpdateWorksiteInput {
	name?: string;
	description?: string;
	status?: WorksiteStatus;
	address?: WorksiteAddress;
	areaSqm?: number;
	startDate?: string;
	expectedEndDate?: string;
	coverImageUrl?: string;
}

export async function updateWorksite(
	id: string,
	input: UpdateWorksiteInput,
): Promise<Worksite> {
	const { worksite } = await handleRequestBody(() =>
		client.patch<WorksiteResponse>(`/worksites/${id}`, {
			name: input.name,
			description: input.description,
			status: input.status,
			address: input.address,
			area_sqm: input.areaSqm,
			start_date: input.startDate,
			expected_end_date: input.expectedEndDate,
			cover_image_url: input.coverImageUrl,
		}),
	);
	return worksite;
}

export async function getWorksite(id: string): Promise<Worksite> {
	const { worksite } = await handleRequestBody(() =>
		client.get<WorksiteResponse>(`/worksites/${id}`),
	);
	return worksite;
}

export interface WorksiteMember {
	user_id: string;
	name: string;
	email: string;
	role: string;
	joined_at: string;
}

export interface ListWorksiteMembersResponse {
	count: number;
	items: WorksiteMember[];
}

export function listWorksiteMembers(
	worksiteId: string,
): Promise<ListWorksiteMembersResponse> {
	return handleRequestBody(() =>
		client.get<ListWorksiteMembersResponse>(`/worksites/${worksiteId}/members`),
	);
}
